import { forwardParseSyncWithSentry as forwardParseSync } from "../../../utils/forwardParserWithSentry";
import type {
  ImportMetaJSON,
  ReplaceMetaJSON,
  ForwardParse,
  ReverseParse
} from "../useStudioAPI.types";
import { processModelCreator } from "../../../models/process.model";
import { dbOps } from "./dbOps.store";
import { studioAPIActions } from "../actions";

const { deleteProcess, importProcess, replaceProcess } = studioAPIActions;

export const useStudioData = () => {
  const importMetaJSON: ImportMetaJSON = ({
    metaJSON,
    processId,
    config = {}
  }) => {
    const {
      shouldSkipDeleteProcess = false,
      shouldSkipUpdatingModifiedAtTimestamp = false,
      shouldLogChangesInLedgerAndUndoStack = false,
      shouldSkipClearingPreviousLedgerAndUndoRedoStack = false
    } = config;

    dbOps().transaction(
      () => {
        if (!shouldSkipDeleteProcess) {
          deleteProcess(processId, {
            shouldLogChangesInLedgerAndUndoStack,
            shouldSkipClearingLedger:
              shouldSkipClearingPreviousLedgerAndUndoRedoStack
          });
        }

        importProcess(processId, metaJSON, {
          shouldSkipUpdatingModifiedAtTimestamp,
          shouldLogChangesInLedgerAndUndoStack,
          shouldSkipClearingPreviousLedgerAndUndoRedoStack
        });
      },
      {
        skipLoggingInLedgerAndUndoStack: !shouldLogChangesInLedgerAndUndoStack
      }
    );
  };

  const replaceMetaJSON: ReplaceMetaJSON = ({
    metaJSON,
    processId,
    config = {}
  }) => {
    const {
      shouldLogChangesInLedgerAndUndoStack = false,
      shouldSkipClearingPreviousLedgerAndUndoRedoStack = false
    } = config;

    dbOps().transaction(
      () => {
        replaceProcess(processId, metaJSON, {
          shouldLogChangesInLedgerAndUndoStack,
          shouldSkipClearingPreviousLedgerAndUndoRedoStack
        });
      },
      {
        skipLoggingInLedgerAndUndoStack: !shouldLogChangesInLedgerAndUndoStack
      }
    );
  };

  const forwardParseMetaJSONToDeployableJSON: ForwardParse = ({
    metaJSON,
    shouldIgnoreInvalidReferences
  }) => {
    return forwardParseSync({ metaJSON, shouldIgnoreInvalidReferences });
  };

  /** Takes deployable json and coverts it into metaJSON */
  const reverseParseDeployableJSONToMetaJSON: ReverseParse = ({
    processId,
    deployableJSON
  }) => {
    return processModelCreator(deployableJSON, { processId });
  };

  return {
    importMetaJSON,
    replaceMetaJSON,
    forwardParseMetaJSONToDeployableJSON,
    reverseParseDeployableJSONToMetaJSON
  };
};
